import React from 'react';
import { StyledSwatch, StyledSwatchFrame, ColorSwatchContainer, ColorSwatchContent, StyledArrowButton, styles } from './SwatchColorCarouselStyles';
import { useSwatchColorCarousel } from './useSwatchColorCarousel';
import type { SwatchColorCarouselProps } from './types';
import { useTranslation } from '@shared/core';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex } from '@withjoy/joykit';
import { ChevronBack, ChevronRight } from '@withjoy/joykit/icons';

const PrimaryControlArrow: React.FC<BoxProps & { $dir: 'left' | 'right' }> = props => {
  return (
    <StyledArrowButton
      __css={styles.arrowButton}
      {...props}
    />
  );
};

export const SwatchColorCarousel: React.FC<SwatchColorCarouselProps> = (props: SwatchColorCarouselProps) => {
  const {
    optionsToRender,
    colorSelected,
    handleMouseClick,
    handleMouseOver,
    handleMouseOut,
    swatchSize,
    nextPos,
    pageRef,
    containerRef,
    handleArrowClick,
    showLeftArrow,
    showRightArrow,
    rootWidth,
  } = useSwatchColorCarousel(props);
  const { checkAvailability = false } = props;
  const { tq } = useTranslation('carousel');

  return (
    <Box
      __css={styles.colorCarousel}
      ref={pageRef}
    >
      <Flex
        __css={styles.colorCarouselContainer}
        ref={containerRef}
        width={rootWidth}
      >
        {showLeftArrow && (
          <PrimaryControlArrow
            $dir="left"
            style={{ left: 0 }}
          >
            <Box
              as="button"
              onClick={() => handleArrowClick('left')}
              aria-label={tq`Previous`}
            >
              <ChevronBack
                aria-hidden
                title={undefined}
                size={swatchSize}
              />
            </Box>
          </PrimaryControlArrow>
        )}
        <ColorSwatchContainer
          __css={styles.colorSwatchContainer}
          $nextPos={nextPos}
          width="100%"
        >
          {optionsToRender.map(option => {
            return (
              (option.color || option.imageUrl) && (
                <Box
                  key={option.id}
                  __css={styles.swatchFrame}
                >
                  <ColorSwatchContent __css={styles.colorSwatchContent}>
                    <StyledSwatchFrame
                      as="button"
                      onClick={(e: React.MouseEvent) => handleMouseClick(e, option)}
                      onMouseOver={(e: React.MouseEvent) => handleMouseOver(e, option)}
                      onMouseOut={(e: React.MouseEvent) => handleMouseOut(e, option)}
                      aria-label={option.label}
                      __css={styles.colorSwatchContentOver}
                    >
                      <StyledSwatch
                        aria-label={option.label}
                        __css={styles.swatch}
                        _after={{
                          opacity: colorSelected === option.id ? 1 : 0,
                        }}
                        data-readonly={(checkAvailability && option.notAvailable) || undefined}
                        size={swatchSize}
                        style={{ backgroundColor: option.color || undefined, backgroundImage: option.imageUrl ? `url(${option.imageUrl})` : undefined }}
                      />
                    </StyledSwatchFrame>
                  </ColorSwatchContent>
                </Box>
              )
            );
          })}
        </ColorSwatchContainer>
        {showRightArrow && (
          <PrimaryControlArrow
            $dir="right"
            style={{ right: 0 }}
          >
            <Box
              as="button"
              onClick={() => handleArrowClick('right')}
              aria-label={tq`Next`}
            >
              <ChevronRight
                aria-hidden
                title={undefined}
                size={swatchSize}
              />
            </Box>
          </PrimaryControlArrow>
        )}
      </Flex>
    </Box>
  );
};
